import { useEffect, useRef, useState } from "react";
import { api, TOKEN_KEY } from "@/app/api";
import { Card } from "@/app/ui";
import { UploadCloud, FileText, Download, RefreshCw, Trash2, Loader2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";

const TYPE_LABEL = {
  passport_identity: "Passport / identity", qualification_certificate: "Qualification certificate",
  transcript: "Transcript", english_certificate: "English-language certificate", cv: "CV",
  personal_statement: "Personal statement", reference: "Reference", funding_document: "Funding document",
  proof_of_address: "Proof of address", other: "Other",
};
const STATUS_STYLE = { uploaded: "bg-sky-50 text-sky-600", processing: "bg-amber-50 text-amber-600",
  verified: "bg-emerald-50 text-emerald-600", requires_attention: "bg-amber-50 text-amber-600", expired: "bg-rose-50 text-rose-600" };
const MAX = 50 * 1024 * 1024;
const ALLOWED = ["pdf", "jpg", "jpeg", "png", "doc", "docx"];
const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export default function Documents() {
  const [docs, setDocs] = useState(null);
  const [types, setTypes] = useState([]);
  const [docType, setDocType] = useState("passport_identity");
  const [displayName, setDisplayName] = useState("");
  const [expiry, setExpiry] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const replaceRef = useRef({});

  const load = async () => { const { data } = await api.get("/individual/documents"); setDocs(data.documents); setTypes(data.types); };
  useEffect(() => { load(); }, []);

  const validate = (f) => {
    const ext = f.name.split(".").pop().toLowerCase();
    if (!ALLOWED.includes(ext)) { toast.error("Allowed: PDF, JPG, JPEG, PNG, DOC, DOCX"); return false; }
    if (f.size > MAX) { toast.error("File exceeds the 50 MB limit"); return false; }
    return true;
  };

  const upload = async () => {
    if (!file) { toast.error("Choose a file first"); return; }
    if (!validate(file)) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file); fd.append("doc_type", docType);
    fd.append("display_name", displayName); fd.append("expiry_date", expiry);
    try { await api.post("/individual/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded"); setFile(null); setDisplayName(""); setExpiry(""); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); } finally { setUploading(false); }
  };

  const replace = async (id, f) => {
    if (!f || !validate(f)) return;
    const fd = new FormData(); fd.append("file", f);
    try { await api.put(`/individual/documents/${id}`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Document replaced"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Replace failed"); }
  };

  const del = async (id) => { if (!window.confirm("Delete this document? This cannot be undone.")) return; await api.delete(`/individual/documents/${id}`); toast.success("Document deleted"); await load(); };

  const downloadUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/individual/documents/${id}/download?auth=${localStorage.getItem(TOKEN_KEY)}`;

  return (
    <div data-testid="documents-page">
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-ink">Document Centre</h1>
      <p className="mt-2 text-slatey">Upload your documents once and reuse them securely across applications.</p>

      <Card className="mt-6" data-testid="upload-card">
        <h2 className="font-display text-lg font-bold text-ink">Upload a document</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <label className="text-sm font-medium text-ink">Document type</label>
            <select value={docType} onChange={(e) => setDocType(e.target.value)} data-testid="upload-type"
              className="mt-1.5 w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-[15px] outline-none focus:border-cobalt">
              {(types.length ? types : Object.keys(TYPE_LABEL)).map((t) => <option key={t} value={t}>{TYPE_LABEL[t] || t}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-ink">Display name (optional)</label>
            <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} data-testid="upload-name"
              className="mt-1.5 w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-[15px] outline-none focus:border-cobalt" placeholder="e.g. Passport" />
          </div>
          <div>
            <label className="text-sm font-medium text-ink">Expiry date (optional)</label>
            <input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} data-testid="upload-expiry"
              className="mt-1.5 w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-[15px] outline-none focus:border-cobalt" />
          </div>
          <div>
            <label className="text-sm font-medium text-ink">File (PDF, JPG, PNG, DOC/DOCX · max 50 MB)</label>
            <input type="file" data-testid="upload-file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              onChange={(e) => setFile(e.target.files[0])}
              className="mt-1.5 w-full text-sm text-slatey file:mr-3 file:rounded-lg file:border-0 file:bg-cobalt/10 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-cobalt" />
          </div>
        </div>
        <button onClick={upload} disabled={uploading} data-testid="upload-btn"
          className="mt-4 inline-flex items-center gap-2 rounded-xl bg-cobalt px-5 py-3 text-sm font-semibold text-white disabled:opacity-50">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Upload
        </button>
      </Card>

      <div className="mt-6 space-y-3">
        {docs === null && <p className="text-slatey">Loading documents…</p>}
        {docs && docs.length === 0 && <Card><p className="text-slatey">No documents yet. Upload your first document above.</p></Card>}
        {docs?.map((d) => (
          <Card key={d.id} data-testid={`doc-${d.id}`} className="flex flex-wrap items-center gap-4">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-cobalt/10 text-cobalt"><FileText className="h-5 w-5" /></span>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-ink break-words">{d.display_name}</p>
              <p className="text-sm text-slatey break-words">{TYPE_LABEL[d.doc_type] || d.doc_type} · {d.file_type?.toUpperCase()} · {fmtSize(d.size)}{d.expiry_date ? ` · expires ${d.expiry_date}` : ""}</p>
            </div>
            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${STATUS_STYLE[d.status] || "bg-slate-100 text-slate-600"}`}>{d.status}</span>
            <div className="flex items-center gap-1">
              <a href={downloadUrl(d.id)} target="_blank" rel="noopener noreferrer" data-testid={`doc-download-${d.id}`} className="rounded-lg p-2 text-slatey hover:text-cobalt" title="Download"><Download className="h-4 w-4" /></a>
              <button onClick={() => replaceRef.current[d.id]?.click()} data-testid={`doc-replace-${d.id}`} className="rounded-lg p-2 text-slatey hover:text-cobalt" title="Replace"><RefreshCw className="h-4 w-4" /></button>
              <input ref={(el) => (replaceRef.current[d.id] = el)} type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" className="hidden" onChange={(e) => e.target.files[0] && replace(d.id, e.target.files[0])} />
              <button onClick={() => del(d.id)} data-testid={`doc-delete-${d.id}`} className="rounded-lg p-2 text-slate-400 hover:text-rose-500" title="Delete"><Trash2 className="h-4 w-4" /></button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
